import React from 'react'
import { Link } from 'react-router-dom'
import managementService from '../../services/managementService'
import ManagementReportShell from './ManagementReportShell'
import useManagementReport from './useManagementReport'
import { DataTable, DrillLinks, HealthBadge, KpiGrid, KpiTile, Section } from './ManagementWidgets'
import { applyTextFilters, at, exportToSpreadsheet, flattenMetrics } from './managementUtils'

const LINKS = [
  { label: 'Subscriptions', to: '/subscriptions' },
  { label: 'Subscription renewals', to: '/subscriptions/renewals' },
  { label: 'Expirations', to: '/subscriptions/expirations' },
  { label: 'Contract renewals', to: '/contracts/contract-renewal' }
]

const daysTone = (days) => (days === null || days === undefined ? 'Grey' : days < 0 ? 'Red' : days <= 30 ? 'Amber' : 'Green')

export const SUBSCRIPTION_COLUMNS = [
  {
    key: 'name',
    label: 'Subscription',
    render: (v, row) => (row.subscriptionId ? <Link to={`/subscriptions/view/${row.subscriptionId}`}>{v || row.subscriptionNumber}</Link> : v)
  },
  { key: 'vendor', label: 'Vendor' },
  { key: 'category', label: 'Category' },
  { key: 'criticality', label: 'Criticality', type: 'health' },
  { key: 'renewalDate', label: 'Renewal', type: 'date' },
  {
    key: 'daysToRenewal',
    label: 'Days',
    align: 'right',
    render: (v) => <HealthBadge status={daysTone(v)} label={v === null || v === undefined ? 'n/a' : v < 0 ? `${Math.abs(v)} overdue` : `${v}`} />
  },
  { key: 'noticeDeadline', label: 'Notice by', type: 'date' },
  { key: 'autoRenewal', label: 'Auto-renew' },
  { key: 'annualCostInr', label: 'Annual (INR)', format: 'currency' },
  { key: 'businessOwner', label: 'Business owner', render: (v) => v || <span className="mis-muted">Missing</span> }
]

const RenewalsCommitmentsReport = () => {
  const report = useManagementReport(managementService.getRenewals)
  const { filters } = report

  const onExport = (data) => exportToSpreadsheet('renewals-commitments', {
    Summary: flattenMetrics(data),
    'Upcoming renewals': data.subscriptions?.upcoming || [],
    'All subscriptions': data.subscriptions?.all || [],
    'Contract renewals': data.customerContracts?.items || []
  })

  return (
    <ManagementReportShell
      title="Renewals & Commitments"
      subtitle="Recurring commitments, renewals due in the next 90 days and customer contract renewals"
      report={report}
      onExport={onExport}
    >
      {(d) => {
        const s = d.subscriptions || {}
        const r = s.renewals || {}
        const cc = d.customerContracts || {}
        return (
          <>
            <DrillLinks links={LINKS} />
            {d.note && <p className="mis-muted" style={{ marginBottom: 12 }}>{d.note}</p>}

            <Section title="Subscription commitments">
              <KpiGrid>
                <KpiTile label="Active subscriptions" metric={s.activeCount} to="/subscriptions/list" />
                <KpiTile label="Monthly commitment" metric={s.monthlyCommitmentInr} format="currency" />
                <KpiTile label="Annual commitment" metric={s.annualCommitmentInr} format="currency" />
                <KpiTile label="Notice deadlines (30d)" metric={s.noticeDeadlinesNext30Days} tone="amber" />
                <KpiTile label="Critical due (30d)" metric={s.criticalDueNext30Days} tone="red" />
                <KpiTile label="Missing business owner" metric={s.missingBusinessOwner} tone="amber" />
                <KpiTile label="Missing finance owner" metric={s.missingFinanceOwner} tone="amber" />
              </KpiGrid>
            </Section>

            <Section title="Renewal horizon">
              <KpiGrid>
                <KpiTile label="Overdue" metric={at(r, 'overdue.count')} tone="red" hint="Past renewal date" />
                <KpiTile label="Overdue value" metric={at(r, 'overdue.annualValueInr')} format="currency" />
                <KpiTile label="Within 30 days" metric={at(r, 'within30Days.count')} tone="amber" />
                <KpiTile label="Value (≤30d)" metric={at(r, 'within30Days.annualValueInr')} format="currency" />
                <KpiTile label="31–60 days" metric={at(r, 'within31to60Days.count')} />
                <KpiTile label="Value (31–60d)" metric={at(r, 'within31to60Days.annualValueInr')} format="currency" />
                <KpiTile label="61–90 days" metric={at(r, 'within61to90Days.count')} />
                <KpiTile label="Value (61–90d)" metric={at(r, 'within61to90Days.annualValueInr')} format="currency" />
              </KpiGrid>
            </Section>

            <Section title="Upcoming renewals" subtitle="Overdue and due within 90 days">
              <DataTable rows={applyTextFilters(s.upcoming, filters)} columns={SUBSCRIPTION_COLUMNS} emptyText="No renewals due in the next 90 days." />
            </Section>

            <div className="mis-two-col">
              <Section title="Commitments by category">
                <DataTable
                  rows={s.byCategory || []}
                  emptyText="No active subscriptions."
                  columns={[
                    { key: 'category', label: 'Category' },
                    { key: 'count', label: 'Subscriptions', format: 'number' },
                    { key: 'monthlyCostInr', label: 'Monthly (INR)', format: 'currency' }
                  ]}
                />
              </Section>
              <Section title="Customer contract renewals" subtitle="Draft, pending or approved, due within 90 days">
                <KpiGrid>
                  <KpiTile label="Pending" metric={cc.pendingRenewals} to="/contracts/contract-renewal" />
                  <KpiTile label="Overdue" metric={cc.overdue} tone="red" />
                  <KpiTile label="Renewal value" metric={cc.renewalValue} format="currency" />
                </KpiGrid>
                <div style={{ marginTop: 12 }}>
                  <DataTable
                    rows={applyTextFilters(cc.items, filters, { customerFields: ['customer'], projectFields: ['projectName'] })}
                    emptyText="No contract renewals due."
                    columns={[
                      { key: 'renewalNumber', label: 'Renewal' },
                      { key: 'customer', label: 'Customer' },
                      { key: 'status', label: 'Status' },
                      { key: 'renewalDate', label: 'Renewal date', type: 'date' },
                      { key: 'renewalValue', label: 'Value', format: 'currency' },
                      { key: 'overdue', label: 'Overdue', render: (v) => (v ? <HealthBadge status="Red" label="Overdue" /> : '—') }
                    ]}
                  />
                </div>
              </Section>
            </div>
          </>
        )
      }}
    </ManagementReportShell>
  )
}

export default RenewalsCommitmentsReport
